import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Save, Loader2, Clock } from 'lucide-react'; 
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import {
  PrefTypeConst,
  PrefCategoryLabel,
  ListingCategoryConst,
  prefectureTypesOf,
  ScheduleCategoryTabs,
} from '../../../constants';
import { 
  BiddingScheduleFlowSteps as OriginalFlowSteps, 
  ScheduleTypeLabel as OriginalLabels,
  BusinessDayTypeConst,
  BusinessDayTypeOptions,
  InitialBiddingScheduleTimes, 
  ScheduleTypeConst,
  TODAY_TASK_TYPES 
} from '../../../constants/BiddingScheduleConst';
import { BiddingScheduleCalendar } from '../../../components/admin/BiddingSchedule/BiddingScheduleCalendar';
import { useAuth } from '../../../contexts/AuthContext';
import { useGetMonthSchedulesQuery, useUpsertSchedulesMutation } from '../../../hooks/useCalendarQuery';
import { type BiddingScheduleUpsertRequest, type BiddingScheduleDayDto } from '../../../types/BiddingScheduleTypes';
import { useAppDialog } from '../../../hooks/useAppDialog';
import { UserRole } from '../../../types/UserRole';
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * 表示月の中で既定の選択日にする日付を返す（表示月が今月なら今日・それ以外は月初）。
 * 「表示月と選択日は必ず一致させる」という画面の性質を1箇所で担保する（D-0050 改訂・D-0055①）。
 */
const defaultDateInMonth = (month: Dayjs): Dayjs =>
  month.isSame(dayjs(), 'month') ? dayjs() : month.startOf('month');

/**
 * 保存ペイロードの `times` を、**画面に出している工程だけ**に絞る（H2）。
 *
 * 🔒 画面に出していない工程を送らないことで、サーバ側の順序検証を経由させない（INV-ST-4）。
 *    入札会の提出締切・採番がこれに当たる——送らなくてもサーバが番兵値で補うため行は消えない（H3）。
 * 🔒 絞り込みの基準は `displaySteps` ただ1つ。表示と送信を別々の条件で書くと将来ずれるため、
 *    「出している工程＝送る工程」を構造で保証する。
 */
const pickTimes = (
  times: Record<number, string> | undefined,
  steps: readonly number[]
): Record<number, string> => {
  const picked: Record<number, string> = {};
  if (!times) return picked;
  steps.forEach(step => {
    if (step in times) picked[step] = times[step];
  });
  return picked;
};

export const BiddingSchedulePage = () => {
  //----------------
  // Context / Hooks
  //----------------
  const queryClient = useQueryClient();
  // ログイン中のユーザー情報を取得
  const { currentUser } = useAuth();
  // スケジュール設定（入札開催や時刻）の保存APIを呼び出すためのミューテーション
  const upsertMutation = useUpsertSchedulesMutation();

  const { showConfirm, OverlayElement } = useAppDialog();
  
  // 権限の判定（管理者は編集可、それ以外は閲覧専用）
  const isAdmin = currentUser?.role === UserRole.Admin;
  const isReadOnly = !isAdmin;

  //----------------
  // State
  //----------------
  // カレンダーで選択された日付の配列（単一選択、または複数一括選択用）
  const [selectedDates, setSelectedDates] = useState<Dayjs[]>([dayjs()]); 
  // カレンダーの表示月を管理
  const [viewDate, setViewDate] = useState<Dayjs>(dayjs()); 
  // 初期化完了フラグ（ユーザー情報等の読み込み完了後にAPIを叩くための制御に使用）
  const [isInitialized, setIsInitialized] = useState(false); 
  // 一度でも月次データを取得できたか（全画面ローディングを初回だけに限定するため・D-0064）
  const hasLoadedOnceRef = useRef(false);
  // 未保存判定のベースライン（取得直後の値・D-0057）。
  // null の間は「まだ取得できていない」＝未保存なしとして扱う。
  const baselineRef = useRef<{ businessStatus: number; holdingStatus: string; scheduleData: string } | null>(null);
  // 選択された日付の営業日区分（営業日 or 休業日）
  const [businessStatus, setBusinessStatus] = useState<number>(BusinessDayTypeConst.BUSINESS_DAY);

  // 表示中の出品区分（通常入札 or 入札会）。取得・保存・列構成のすべてがこれに従う
  const [listingCategory, setListingCategory] = useState<number>(ListingCategoryConst.BIDDING);

  // 表示中の区分が持つ県区分の列（通常入札=県内・県外／入札会=指定なしの1列）
  const prefTypes = useMemo(() => prefectureTypesOf(listingCategory), [listingCategory]);

  // 系列ごとの「入札開催」トグルの状態（true: 開催, false: 未開催）
  const createEmptyHoldingStatus = () => ({
    [PrefTypeConst.NONE]: false,
    [PrefTypeConst.INTERNAL]: false,
    [PrefTypeConst.EXTERNAL]: false,
  });
  const [holdingStatus, setHoldingStatus] = useState<Record<number, boolean>>(createEmptyHoldingStatus());

  // 入札時刻設定の初期値（空の時刻データ）を生成するヘルパー関数
  const createEmptyScheduleData = () => JSON.parse(JSON.stringify(InitialBiddingScheduleTimes));
  
  // 県区分別・工程別の設定時刻データを保持
  const [scheduleData, setScheduleData] = useState<Record<number, Record<number, string>>>(createEmptyScheduleData());
  // 既に実行済みの実績時刻（採番時刻、落札確定時刻など）を保持
  const createEmptyExecutedData = (): Record<number, Record<number, string | null>> =>
    ({ [PrefTypeConst.NONE]: {}, [PrefTypeConst.INTERNAL]: {}, [PrefTypeConst.EXTERNAL]: {} });
  const [executedData, setExecutedData] = useState<Record<number, Record<number, string | null>>>(createEmptyExecutedData());

  // 現在選択されている設定が休業日かどうかを判定
  const isNonBusinessDay = businessStatus === BusinessDayTypeConst.NON_BUSINESS_DAY;

  //----------------
  // 状態判定 
  //----------------
  
  // 選択された日付に「過去日」が含まれているかを判定
  // （過去日が含まれる場合は、変更できないよう画面全体をロックする）
  const isPastSelected = useMemo(() => {
    return selectedDates.some(d => d.isBefore(dayjs(), 'day'));
  }, [selectedDates]);

  // 県区分別（県内/県外）に「落札結果確定」の実績が記録されているかを判定
  // （確定済みの場合は設定を変更できないようトグル等をロックする）
  const isConfirmed = useMemo(() => {
    const map: Record<number, boolean> = {};
    prefTypes.forEach(pref => {
      map[pref] = !!executedData[pref]?.[ScheduleTypeConst.BIDDING_CONFIRM];
    });
    return map;
  }, [executedData, prefTypes]);

  // 表示中の区分で1つでも落札確定の実績があるか判定
  // （画面上部の注意書きテキストの表示切り替えに使用）
  const hasAnyExecutedData = useMemo(() => {
    return prefTypes.some(pref => isConfirmed[pref]);
  }, [isConfirmed, prefTypes]);
  
  // 権限と出品区分ごとに表示する工程（ステップ）の配列を決定する
  //
  // 🔒 H1: 入札会は「提出締切」「入札番号採番」を業務上持たないため、**誰に対しても表示しない**
  //    （D-0072・D-0078）。照会側（売手・生産者）にも提出締切を出さないのは、入札会に
  //    その工程が存在しない以上、無意味な値（01:00）を見せることになるため。
  // 🔒 **displaySteps は「画面に出す工程」と「保存時に送る工程」の唯一の出所**とする（H2 とセット）。
  //    保存ペイロードは本配列から組み立てるので、表示と送信が構造的にずれない。
  // ⚠ JA のロール分岐は**現行のまま**（＝市場と同じ並び）。設計 §3 の式は JA を売手・生産者と
  //    同じ扱いにしていたが、現行コードは JA を分岐しておらず、そのとおり実装すると
  //    「JA の通常入札が 6→4 工程」という**本要件と無関係な挙動変更**が混ざる。
  //    2026-09-16 多々良の選択＝案B（区分の分岐だけ足す）。JA に採番を見せてよいかは P-0045。
  const displaySteps = useMemo(() => {
    const role = currentUser?.role;
    const isSale = listingCategory === ListingCategoryConst.SALE;

    if (role === UserRole.Buyer) {
      // 買手の場合
      return [
        ScheduleTypeConst.PUBLISHED_START_AT, // ※出品予定公開
        ScheduleTypeConst.BIDDING_START,      // ※入札開始
        ScheduleTypeConst.BIDDING_END         // ※入札終了
      ];
    }

    if (role === UserRole.Seller || role === UserRole.Farmer) {
      // 売手・生産者の場合（入札会は提出締切を持たないため3工程）
      return isSale
        ? [
            ScheduleTypeConst.LOWEST_PRICE_DECISION_END,  // ※最低落札値締切
            ScheduleTypeConst.BIDDING_START,              // ※入札開始
            ScheduleTypeConst.BIDDING_END                 // ※入札終了
          ]
        : [
            ScheduleTypeConst.APPLIED_END,                // ※提出締切
            ScheduleTypeConst.LOWEST_PRICE_DECISION_END,  // ※最低落札値締切
            ScheduleTypeConst.BIDDING_START,              // ※入札開始
            ScheduleTypeConst.BIDDING_END                 // ※入札終了
          ];
    }

    // 管理者（Admin）・JA の場合（入札会は提出締切・採番を持たないため4工程）
    return isSale
      ? OriginalFlowSteps.filter(
          step => step !== ScheduleTypeConst.APPLIED_END && step !== ScheduleTypeConst.NUMBERING
        )
      : OriginalFlowSteps;
  }, [currentUser?.role, listingCategory]);

  //----------------
  // Memo / Data Fetching
  //----------------
  // カレンダーの表示月（viewDate）から、APIリクエスト用の年月パラメータ（YYYYMM形式）を生成
  const yearMonth = useMemo(() => parseInt(viewDate.format("YYYYMM")), [viewDate]);
  
  // 表示月の1ヶ月分のスケジュールデータを取得
  // （ユーザー情報が存在し、初期化が完了している場合のみリクエストを実行）
  const { data: monthData, isLoading: isDataLoading, isFetching, error } = useGetMonthSchedulesQuery(
    yearMonth,
    listingCategory,
    { enabled: !!currentUser && isInitialized }
  );
  
  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // 1. 一覧データのキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['monthSchedules'] }); 
    
    // 2. ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  //----------------
  // Effect (データ同期)
  //----------------
  // ユーザー情報を取得後、初期化完了フラグを立ててAPI通信を許可する
  useEffect(() => {
    if (!currentUser || isInitialized) return;
    setIsInitialized(true); 
  }, [currentUser, isInitialized]);

  // データ取得時のエラーハンドリング
  useEffect(() => {
    if (error) {
      toast.error(error instanceof Error ? error.message : "スケジュールの取得に失敗しました");
    }
  }, [error]);

  // カレンダーの選択日付や、APIからの月間データが変化した際に実行
  // 選択された日（先頭の日付）のデータを画面の各入力フォームのState（営業日、開催トグル、設定時刻、実績）に反映させる
  useEffect(() => {
    const typedMonthData = monthData as Record<string, BiddingScheduleDayDto> | undefined;
    if (!typedMonthData || selectedDates.length === 0 || !selectedDates[0]) return;
    
    // 選択された日付をキーにして、月間データの中から該当日のデータを抽出
    const dateKey = selectedDates[0].format("YYYY-MM-DD");
    const dayData = typedMonthData[dateKey];

    // 🔒 選択日のデータが取得できていないときは state を上書きしない（D-0055②・INV-SCH-10）。
    // 空の器で上書きすると「営業日・全区分非開催・時刻すべて空」が画面の値になり、
    // そのまま保存すると既存のスケジュール行を消してしまう。
    // 月送りで選択を表示月に閉じている（D-0055①）ため通常ここには到達しないが、
    // 「表示と選択日がずれて見える」より「既存データを消さない」を優先する。
    if (!dayData) return;

    // 次にStateにセットするための器を初期化
    let nextScheduleData = createEmptyScheduleData();
    let nextExecutedData: Record<number, Record<number, string | null>> = createEmptyExecutedData();
    let nextBusinessStatus: number = BusinessDayTypeConst.BUSINESS_DAY;
    let nextHolding: Record<number, boolean> = createEmptyHoldingStatus();

    // 該当日のデータが存在すれば、器に値を詰め込んでいく
    if (dayData) {
      nextBusinessStatus = dayData.businessDay;
      if (dayData.holdingStatus) {
        Object.entries(dayData.holdingStatus).forEach(([k, v]) => { nextHolding[Number(k)] = v as boolean; });
      }
      if (dayData.scheduleData) {
        Object.entries(dayData.scheduleData).forEach(([prefKey, times]) => {
          const pKey = Number(prefKey);
          if (nextScheduleData[pKey]) {
            Object.entries(times as Record<number, string>).forEach(([typeKey, timeStr]) => {
              if (timeStr) nextScheduleData[pKey][Number(typeKey)] = timeStr;
            });
          }
        });
      }
      if (dayData.executedData) {
        Object.entries(dayData.executedData).forEach(([prefKey, times]) => {
          const pKey = Number(prefKey);
          nextExecutedData[pKey] = {};
          Object.entries(times as Record<number, string | null>).forEach(([typeKey, timeStr]) => {
            nextExecutedData[pKey][Number(typeKey)] = timeStr;
          });
        });
      }
    }
    
    // 抽出・整形したデータを一斉にStateに反映
    setBusinessStatus(nextBusinessStatus);
    setHoldingStatus(nextHolding);
    setScheduleData(nextScheduleData);
    setExecutedData(nextExecutedData);

    // 未保存判定のベースライン（D-0057・タスク8-c）。
    // 画面に流し込んだ「取得直後の値」をそのまま控え、以後の現在値と比較して未保存編集を判定する。
    // 保存に成功すると monthSchedules が invalidate され再取得→本 effect が再実行されるため、
    // ベースラインは自動で最新に入れ替わる（保存後にフラグを下ろす処理を別に持つ必要がない）。
    baselineRef.current = {
      businessStatus: nextBusinessStatus,
      holdingStatus: JSON.stringify(nextHolding),
      scheduleData: JSON.stringify(nextScheduleData),
    };
  }, [selectedDates, monthData]);

  //----------------
  // 未保存編集のガード（D-0057・案ii＝確認ダイアログ）
  //----------------
  // 取得直後の値（ベースライン）と現在の入力値を比べて、未保存の編集があるかを判定する。
  // 入力を元に戻せば未保存ではなくなる（フラグ方式と違い「戻したのに警告が出る」が起きない）。
  const isDirty = useCallback(() => {
    const base = baselineRef.current;
    if (!base) return false; // 取得前は比較対象が無い＝未保存なしとして扱う
    return (
      base.businessStatus !== businessStatus ||
      base.holdingStatus !== JSON.stringify(holdingStatus) ||
      base.scheduleData !== JSON.stringify(scheduleData)
    );
  }, [businessStatus, holdingStatus, scheduleData]);

  // 未保存の編集があるときだけ確認ダイアログを挟んでから action を実行する。
  // 🔒 対象は4経路のみ（タブ切替・月送り・単一選択での日付変更・「今日の日付を選択」）。
  //    一括モードでの日付追加は対象外＝1クリックごとにダイアログが出て操作が成立しないため（D-0057）。
  const withUnsavedGuard = useCallback((action: () => void) => {
    if (isReadOnly || !isDirty()) { action(); return; }
    showConfirm({
      title: "未保存の変更があります",
      message: "入力した内容は保存されていません。破棄して移動しますか？",
      onConfirm: action,
    });
  }, [isReadOnly, isDirty, showConfirm]);

  //----------------
  // Handlers
  //----------------
  // 営業日区分の変更ハンドラー
  const handleBusinessStatusChange = useCallback((status: number) => {
    setBusinessStatus(status);
    // 非営業日に変更された場合は「入札開催」を強制的にオフにする。
    // 営業区分はカレンダー共通の値なので入札会も含めて全区分を落とす（D-0053・INV-SCH-3）。
    if (status === BusinessDayTypeConst.NON_BUSINESS_DAY) {
      setHoldingStatus(createEmptyHoldingStatus());
    }
  }, []);

  // 出品区分（タブ）の切替。表示月は維持したまま選択日だけを表示月内の単独選択に戻し、
  // その区分の月次データを取り直す（D-0050 改訂）。
  // 一括選択モードのスイッチ自体は維持する（区分をまたいで続けて一括登録できるようにするため）。
  // 未保存の編集があれば破棄前に確認する（D-0057 経路1）。同じタブの再クリックは何もしない。
  const handleCategoryChange = useCallback((category: number) => {
    if (listingCategory === category) return;
    withUnsavedGuard(() => {
      setSelectedDates([defaultDateInMonth(viewDate)]);
      setListingCategory(category);
    });
  }, [listingCategory, viewDate, withUnsavedGuard]);

  // カレンダーの表示月の変更（前月/次月ボタン・年月ピッカー）。
  // 🔒 月が変わったら選択日を表示月の中に閉じ直す（D-0055①・INV-SCH-10）。
  // 表示月と選択日がずれたままだと、画面には表示月のデータが出ているのに
  // 保存は選択日に対して行われ、別の月の値を書き込む破壊操作になる。
  // 月をまたいだ一括設定は「できない仕様」として確定している（P-0029）。
  // 未保存の編集があれば破棄前に確認する（D-0057 経路2）。月が変わらない場合は確認しない。
  const handleViewDateChange = useCallback((next: Dayjs) => {
    if (viewDate.isSame(next, 'month')) { setViewDate(next); return; }
    withUnsavedGuard(() => {
      setViewDate(next);
      setSelectedDates([defaultDateInMonth(next)]);
    });
  }, [viewDate, withUnsavedGuard]);

  // カレンダー上の日付クリック時のハンドラー。
  // 単一選択モードでは未保存の編集があれば破棄前に確認する（D-0057 経路3）。
  // 🔒 一括モードでの日付追加・解除は確認の対象外（1クリックごとに出ると操作が成立しないため）。
  const handleDateToggle = useCallback((date: Dayjs, isBulkMode: boolean) => {
    if (isReadOnly || !isBulkMode) {
      withUnsavedGuard(() => setSelectedDates([date]));
      return;
    }
    // 一括モードの場合は、日付選択済みなら解除、未選択なら選択のトグル動作
    setSelectedDates(prev => {
      const isAlreadySelected = prev.some(d => d.isSame(date, 'day'));
      return isAlreadySelected ? (prev.length > 1 ? prev.filter(d => !d.isSame(date, 'day')) : prev) : [...prev, date];
    });
  }, [isReadOnly, withUnsavedGuard]);

  // 設定保存ボタンのクリックハンドラー
  const handleSave = useCallback(() => {
    // 保存対象の日付が選択されていない場合はガード
    if (selectedDates.length === 0) { toast.error("日付を選択してください"); return; }

    // 単一日か複数日（一括）かで確認メッセージを出し分ける
    const confirmMsg = selectedDates.length > 1 
        ? `${selectedDates.length}日分の設定を一括更新します。よろしいですか？`
        : `${selectedDates[0].format('YYYY年MM月DD日')} の設定を更新します。よろしいですか？`;

    showConfirm({
      title: "入札スケジュール管理",
      message: confirmMsg,
      onConfirm: async () => {
        // APIに送信するリクエストボディを構築
        // 保存するのは表示中の区分の系列だけ（他区分の開催フラグはサーバ側で保持される＝INV-SCH-1）
        const payload: BiddingScheduleUpsertRequest = {
          targetDates: selectedDates.map(d => parseInt(d.format("YYYYMMDD"))),
          businessDay: businessStatus,
          listingCategory: listingCategory,
          settings: prefTypes.map(pref => ({
            prefectureType: pref,
            isHolding: holdingStatus[pref],
            // 🔒 H2: **画面に出している工程だけを送る**（displaySteps が唯一の出所）。
            //    入札会では提出締切・採番が displaySteps に含まれないため送られず、
            //    サーバ側の順序検証（提出締切＜採番＜公開）を経由しない（INV-ST-4）。
            //    送られなかった2工程はサーバが番兵値で補う（H3・UPS-016）ので行は消えない。
            times: pickTimes(scheduleData[pref], displaySteps)
          }))
        };

        try {
          // 構築したデータでAPIを呼び出し、保存処理を実行
          await upsertMutation.mutateAsync(payload);
          toast.success("設定を保存しました");
        } catch (e) { 
          //toast.error("保存に失敗しました"); 
        }
      }
    });
  }, [selectedDates, businessStatus, holdingStatus, scheduleData, upsertMutation, showConfirm, listingCategory, prefTypes, displaySteps]);

  // 列テンプレートは Tailwind が静的解析するため、動的に組み立てず literal を出品区分ごとに持つ
  // （通常入札=工程名＋スペーサー＋県内＋県外の4カラム／入札会=工程名＋スペーサー＋時刻の3カラム）
  const GRID_BIDDING = "grid grid-cols-1 sm:grid-cols-[1fr_110px_minmax(160px,180px)_minmax(160px,180px)] gap-x-4 lg:gap-x-6 items-center";
  const GRID_SALE = "grid grid-cols-1 sm:grid-cols-[1fr_110px_minmax(160px,180px)] gap-x-4 lg:gap-x-6 items-center";
  const mainGridClass = listingCategory === ListingCategoryConst.SALE ? GRID_SALE : GRID_BIDDING;

  //----------------
  // Render Helpers
  //----------------
  // 全画面のローディングは「本当の初回」だけに限定する（D-0064）。
  // queryKey は ['monthSchedules', 年月, 区分] なので、月送りでもタブ切替でもキーが変わり
  // monthData が一旦 undefined に戻る。条件を絞らないとそのたびにページ全体が
  // スピナー画面に置き換わり、画面が点滅して見える。
  // 一度でも取得できた後は、カードを表示したままカード内オーバーレイで待つ。
  if (monthData) hasLoadedOnceRef.current = true;
  const isPageLoading = !isInitialized || (isDataLoading && !monthData && !hasLoadedOnceRef.current);
  if (isPageLoading) return <div className="bg-slate-50 min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>;

  return (
    <div className="bg-slate-50 min-h-screen font-sans overflow-x-hidden text-slate-800 relative">
      
      {/* 共通背景レイヤー */}
      {OverlayElement}

      {/* lg 以上ではカードをビューポート高さに収める（lg:h-screen ＋ カードの lg:max-h-full）。
          収まりきらない場合は左右の列が内側でスクロールする（切れて見えないようにするため）。
          余白は 2026-09-11 に全体を詰めた（カレンダーのセル高 64px は要件により維持） */}
      <div className="max-w-350 mx-auto px-4 lg:px-8 pt-3 lg:pt-4 pb-3 flex flex-col lg:h-screen">
        
        {/* ヘッダー */}
        <header className="mb-3 lg:mb-4 shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-2">
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-800">
              {currentUser?.role === UserRole.Admin ? '入札スケジュール管理' : '入札スケジュール確認'}
            </h1>

            {/* 出品区分の切替（通常入札 / 入札会）。
                ヘッダー行に置くことでカードの高さを削らず、カードの角丸とも干渉しない */}
            <div
              className="flex shrink-0 rounded-xl border border-slate-200 bg-slate-100 p-0.5 shadow-sm"
              role="tablist"
              aria-label="出品区分"
            >
              {ScheduleCategoryTabs.map(tab => {
                const isActive = listingCategory === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleCategoryChange(tab.value)}
                    className={`px-5 py-1.5 text-sm font-black rounded-lg transition-colors ${
                      isActive
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* 管理者以外は「保存ボタン」を非表示にする */}
          {!isReadOnly && (
            <button 
              type="button" 
              onClick={handleSave} 
              // 過去日、保存処理中、および月次データの取得中は保存ボタンを無効化する。
              // 🔒 取得中は画面に「前の月・前の区分の値」が残っているため、
              //    そのまま保存すると別の対象の値を書き込む（D-0055③・INV-SCH-10）。
              disabled={upsertMutation.isPending || isPastSelected || isFetching}
              className="w-full sm:w-auto px-6 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md transition active:scale-95 flex items-center justify-center gap-2 disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              {upsertMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              設定を保存
            </button>
          )}
        </header>

        <main className="flex-1 min-h-0 pb-2">
          <div className="bg-white border border-slate-200 rounded-3xl lg:rounded-4xl shadow-sm flex flex-col lg:flex-row items-stretch overflow-hidden h-fit lg:max-h-full">
            
            {/* 左側：カレンダー */}
            <div className="w-full lg:w-110 bg-slate-50/50 border-b lg:border-b-0 lg:border-r border-slate-100 p-4 lg:p-3 flex flex-col justify-center lg:justify-start shrink-0 lg:overflow-y-auto custom-scrollbar">
              <div className="w-full">
                <BiddingScheduleCalendar 
                  selectedDates={selectedDates} onDateToggle={handleDateToggle} 
                  onReset={() => withUnsavedGuard(() => { setSelectedDates([dayjs()]); setViewDate(dayjs()); })}
                  onCollapseSelection={() => setSelectedDates([defaultDateInMonth(viewDate)])}
                  viewDate={viewDate} onViewDateChange={handleViewDateChange} monthData={monthData}
                  listingCategory={listingCategory}
                />
              </div>
            </div>

            {/* 右側：メイン設定エリア */}
            <div className="flex-1 flex flex-col min-h-0 bg-slate-50 relative overflow-x-hidden sm:overflow-x-auto custom-scrollbar">
              <div className="p-3 lg:p-5 flex-1 flex flex-col min-w-0 sm:min-w-162.5">
                
                {/* データの再取得中や保存処理中は、操作できないようにオーバーレイを表示。
                    月送り・タブ切替・手動リフレッシュのいずれでも覆えるよう isFetching を見る（D-0064） */}
                {(isFetching || upsertMutation.isPending) && (
                  <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-[1px]">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  </div>
                )}

                {/* 上部：メイン設定パネル */}
                <div className={`${mainGridClass} px-1 lg:px-3 border-b border-slate-100 pb-2.5 mb-1`}>
                  <div className="text-base font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                    設定項目
                    {selectedDates.length === 1 && selectedDates[0] && (
                      <span className="text-base font-bold text-indigo-600 ml-1 whitespace-nowrap">{selectedDates[0].format('YYYY年MM月DD日分')}</span>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-1">
                    {/* 過去日の場合は休業日設定のセレクトボックスをロック */}
                    <select 
                      value={businessStatus} 
                      onChange={(e) => handleBusinessStatusChange(Number(e.target.value))} 
                      // disabled={isPastSelected}
                      // 過去日、または閲覧専用ユーザーはロック
                      disabled={isPastSelected || isReadOnly}
                      className="w-full bg-white border border-slate-200 text-sm font-black rounded-lg px-2 py-1.5 outline-none text-slate-600 shadow-sm disabled:bg-slate-100 disabled:text-slate-700 disabled:appearance-none"
                    >
                      {BusinessDayTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>

                  {/* 表示中の区分の系列ごとの「入札開催」トグルスイッチ */}
                  {prefTypes.map(pref => (
                    <div key={`header-${pref}`} className="flex sm:flex flex-col gap-1.5 bg-white px-3 py-2 rounded-2xl border border-slate-200 shadow-sm items-center">
                      <span className={`text-[11px] font-black leading-none text-indigo-600 text-center`}>
                        {listingCategory === ListingCategoryConst.SALE
                          ? "入札会開催"
                          : `${PrefCategoryLabel[pref as keyof typeof PrefCategoryLabel]}入札開催`}
                      </span>
                      <div className="flex flex-col items-center gap-0.5">
                        <button 
                          type="button" 
                          // 閲覧専用ユーザーはトグルスイッチをロック
                          // 過去日、休業日、または既に確定実績がある場合はトグルを固定
                          // disabled={isNonBusinessDay || isPastSelected || isConfirmed[pref as keyof typeof isConfirmed]} 
                          disabled={isNonBusinessDay || isPastSelected || isConfirmed[pref as keyof typeof isConfirmed] || isReadOnly}
                          onClick={() => setHoldingStatus(prev => ({ ...prev, [pref]: !prev[pref] }))} 
                          className={`relative inline-flex h-4.5 w-8.5 shrink-0 items-center rounded-full transition-colors ${holdingStatus[pref] ? 'bg-indigo-600' : 'bg-slate-300'} disabled:opacity-30`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${holdingStatus[pref] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="px-2 py-1.5">
                  <p className="text-[11px] font-bold text-red-500 leading-relaxed">
                      {isReadOnly
                        ? "※この画面は参照専用です。設定の変更はできません。"
                        : isPastSelected
                          ? "※過去日付選択中のため、すべての項目が変更不可（参照のみ）となります。"
                            : isNonBusinessDay
                              ? "※休業日のため、設定は行えません。"
                              : hasAnyExecutedData 
                                ? "※落札確定済のため、入札開催スイッチは固定されています。"
                                : "※営業日であれば全項目の入力が可能です。※「入札開催」がオフの場合、入札日項目はロックされます。"}
                  </p>
                </div>

                {/* 工程リストエリア：時刻の設定フォーム */}
                <div className="flex-1 lg:overflow-y-auto sm:pr-1 custom-scrollbar pt-1">
                  <div className="space-y-1.5 mb-4">
                    {displaySteps.map((type) => {
                      const typeNum = type as number;
                      const isTodayTask = TODAY_TASK_TYPES.includes(typeNum);

                      return (
                        <div key={type} className={`${mainGridClass} border rounded-xl p-2.5 bg-white border-slate-100 hover:border-indigo-100 shadow-sm transition-all`}>
                          <div className="min-w-0 flex items-center gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isTodayTask ? 'bg-indigo-400' : 'bg-emerald-400'}`} />
                            <div className={`text-sm font-bold text-slate-700 truncate`}>{OriginalLabels[typeNum as keyof typeof OriginalLabels]}</div>
                          </div>
                          <div className="hidden sm:block" />
                          
                          {prefTypes.map(pref => {
                            // ・当日タスクの場合：営業日 かつ 入札開催がオン
                            // ・次回タスクの場合：常にアクティブ (休業日や開催オフでも設定可)
                            const isTaskActive = isTodayTask ? (!isNonBusinessDay && holdingStatus[pref]) : true;
                            
                            // 最終的な編集可能フラグ (過去日の場合はすべてブロック)
                            const canEdit = !isPastSelected && isTaskActive;
                            
                            const timeValue = scheduleData[pref]?.[typeNum] || "";

                            return (
                                  <div 
                                    key={`${type}-${pref}`} 
                                    // opacity-40を外し、編集不可時は背景を少しグレー(bg-slate-100)にして視認性を確保
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                                      canEdit 
                                        ? 'bg-slate-50 border-slate-200 focus-within:border-indigo-500 focus-within:bg-white shadow-inner' 
                                        : 'bg-slate-100 border-slate-200'
                                    }`}
                                  >
                                    <span className="text-[11px] font-bold text-slate-400 shrink-0 w-8">
                                      {PrefCategoryLabel[pref as keyof typeof PrefCategoryLabel]}
                                    </span>                                    
                                    {!isReadOnly ? (
                                      // 編集可能な場合は input(time) を表示
                                      <input 
                                        type="time" 
                                        disabled={!canEdit} 
                                        value={timeValue} 
                                        onChange={(e) => setScheduleData(prev => ({ ...prev, [pref]: { ...prev[pref], [typeNum]: e.target.value } }))} 
                                        className="bg-transparent border-none p-0 text-sm font-black text-slate-800 outline-none w-full cursor-pointer disabled:text-slate-400"
                                      />
                                    ) : (
                                      // ▼ 閲覧専用ユーザー：spanでテキスト表示
                                      <span className={`text-sm w-full text-center ${
                                        isTaskActive
                                          ? (timeValue ? 'font-black text-slate-800' : 'font-black text-slate-300') // 有効な項目
                                          : 'font-medium text-slate-300' // 無効な項目（休業日や入札オフなど）
                                      }`}>
                                        {timeValue || "--:--"}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                        </div>
                      );
                    })}
                  </div>
                  {/* 管理者（編集可能なユーザー）のみ表示 ▼▼▼ */}
                  {!isReadOnly && (
                    <div className="mt-4 pt-4 border-t-2 border-dashed border-slate-200 space-y-2">
                      <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest px-2 mb-2 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" /> 実行済み時刻 (実績)
                      </h3>
                      
                      {/* 採番実績 */}
                      <div className={`${mainGridClass} bg-slate-100/50 border border-slate-200/50 rounded-2xl p-2`}>
                        <div className="flex items-center gap-3 min-w-0 px-2">
                          <div className="text-sm font-black text-slate-700 uppercase leading-tight">次回入札日分入札番号採番時刻</div>
                        </div>
                        <div className="hidden sm:block" />
                        {prefTypes.map(pref => (
                          <div key={`exec1-${pref}`} className="flex items-center px-3 py-1.5 rounded-xl border bg-white shadow-sm border-slate-200">
                            <span className="text-[11px] font-bold text-slate-400 shrink-0 w-8">
                                {PrefCategoryLabel[pref as keyof typeof PrefCategoryLabel]}
                            </span>
                            <span className="text-lg font-black text-slate-600 tracking-tight leading-none">
                              {executedData[pref]?.[ScheduleTypeConst.NUMBERING] || "--:--"}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* 確定実績 */}
                      <div className={`${mainGridClass} bg-slate-100/50 border border-slate-200/50 rounded-2xl p-2`}>
                        <div className="flex items-center gap-3 min-w-0 px-2">
                          <div className="text-sm font-black text-slate-700 uppercase leading-tight">落札結果確定時刻</div>
                        </div>
                        <div className="hidden sm:block" />
                        {prefTypes.map(pref => (
                          <div key={`exec7-${pref}`} className="flex items-center px-3 py-1.5 rounded-xl border bg-white shadow-sm border-slate-200">
                            <span className="text-[11px] font-bold text-slate-400 shrink-0 w-8">
                                {PrefCategoryLabel[pref as keyof typeof PrefCategoryLabel]}
                            </span>
                            <span className="text-lg font-black text-slate-600 tracking-tight leading-none">
                              {executedData[pref]?.[ScheduleTypeConst.BIDDING_CONFIRM] || "--:--"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isFetching || isPageLoading}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-30 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isFetching ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default BiddingSchedulePage;